import { useState, useEffect, useRef } from 'react';
import {
  FundChartPeriod,
  FundChartsData,
  UseFundChartResult,
} from '../../types/fundsummary.type';
import { getFundChart } from '../../api/fund.api';
import { WidgetError } from '../../types/common.type';

const formatYMD = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${dd}`;
};

const PERIOD_MONTHS: Record<FundChartPeriod, number | null> = {
  '3M': 3,
  '6M': 6,
  '1Y': 12,
  '3Y': 36,
  '5Y': 60,
  All: null,
};

const resolveRange = (
  period: FundChartPeriod,
): { from?: string; to: string } => {
  const today = new Date();
  const months = PERIOD_MONTHS[period];
  if (months == null) {
    return { to: formatYMD(today) };
  }
  const from = new Date(today);
  from.setMonth(from.getMonth() - months);
  return { from: formatYMD(from), to: formatYMD(today) };
};

const useFundChartData = (
  ric: string = '',
  token: string = '',
  period: FundChartPeriod = '1Y',
  saleInCountryCode: string = 'US',
  onError?: (error: WidgetError) => void,
): UseFundChartResult => {
  const [data, setData] = useState<FundChartsData>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!ric) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    setIsLoading(true);
    setError(null);

    const { from, to } = resolveRange(period);

    Promise.allSettled([
      getFundChart({ token, ric, type: 'nav', from, to, saleInCountryCode }),
      getFundChart({ token, ric, type: 'tna', from, to, saleInCountryCode }),
      getFundChart({ token, ric, type: 'mid', from, to, saleInCountryCode }),
    ])
      .then(([navResult, tnaResult, midResult]) => {
        if (cancelled) return;

        setData({
          navChart:
            navResult.status === 'fulfilled'
              ? navResult.value?.data
              : undefined,
          tnaChart:
            tnaResult.status === 'fulfilled'
              ? tnaResult.value?.data
              : undefined,
          midChart:
            midResult.status === 'fulfilled'
              ? midResult.value?.data
              : undefined,
        });

        const failures = [navResult, tnaResult, midResult].filter(
          (r): r is PromiseRejectedResult => r.status === 'rejected',
        );
        if (failures.length === 3) {
          const err = failures[0].reason as Error & {
            code?: string | number;
          };
          setError(err?.message ?? null);
          onErrorRef.current?.({
            message: err?.message,
            type: `${err?.name}_${err?.code}`,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, ric, period, saleInCountryCode]);

  return { data, isLoading, error };
};

export default useFundChartData;
